import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth';
import { useTheme } from '@/theme';
import { useTranslation } from '@/i18n';
import { useBreakpoint } from '@/hooks';
import { settingsService } from '@/api/services/settings.service';
import {
  USER_SETTINGS,
  getClientQuestionnaireLandingValue,
  getInternalQuestionnaireLandingValue,
  getUserSettingKeys,
} from '@/features/settings/constants';
import { Spinner } from '@/components/ui/Spinner';
import { settingsKeys } from '@/api/hooks/useSettings';
import { useMotrQDashboardQuestionnaires } from '@/features/dashboard/hooks';
import { PERMISSIONS } from '@/routes/routes';

interface MySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserSettingsState {
  firstWarningDueDays: string;
  secondWarningDueDays: string;
  showDashboardCharts: string;
  defaultLandingPage: string;
  clientDashboardLandingPage: string;
  jobEmailsEnabled: string;
  receiveChecklistEmails: string;
  staleDaysThreshold: string;
}

const DEFAULT_STATE: UserSettingsState = {
  firstWarningDueDays: '5',
  secondWarningDueDays: '1',
  showDashboardCharts: 'Show',
  defaultLandingPage: 'todo',
  clientDashboardLandingPage: 'requests',
  jobEmailsEnabled: 'true',
  receiveChecklistEmails: 'true',
  staleDaysThreshold: '7',
};

export const MySettingsModal = ({ isOpen, onClose }: MySettingsModalProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canOpenFrontDoor =
    hasPermission(PERMISSIONS.CLIENT_DASHBOARD.SERVICE_REQUESTS)
    && hasPermission(PERMISSIONS.MOTRQ.SUBMIT_QUESTIONNAIRE);
  const canOpenInternalQuestionnaire =
    hasPermission(PERMISSIONS.DASHBOARD.SERVICE_REQUESTS)
    && hasPermission(PERMISSIONS.MOTRQ.SUBMIT_QUESTIONNAIRE);
  const {
    questionnaires: dashboardQuestionnaires,
    isLoading: isLoadingQuestionnaires,
  } = useMotrQDashboardQuestionnaires(
    isOpen && (canOpenFrontDoor || canOpenInternalQuestionnaire),
  );

  const [settings, setSettings] = useState<UserSettingsState>(DEFAULT_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load settings when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    settingsService
      .getUserSettings(getUserSettingKeys())
      .then((result) => {
        const find = (name: string) => result.find((s) => s.name === name)?.value || '';
        setSettings({
          firstWarningDueDays: find(USER_SETTINGS.FIRST_WARNING_DUE_DAYS) || '5',
          secondWarningDueDays: find(USER_SETTINGS.SECOND_WARNING_DUE_DAYS) || '1',
          showDashboardCharts: find(USER_SETTINGS.SHOW_DASHBOARD_CHARTS) || 'Show',
          defaultLandingPage: find(USER_SETTINGS.DEFAULT_LANDING_PAGE) || 'todo',
          clientDashboardLandingPage: find(USER_SETTINGS.CLIENT_DASHBOARD_LANDING_PAGE) || 'requests',
          jobEmailsEnabled: find(USER_SETTINGS.JOB_EMAILS_ENABLED) || 'true',
          receiveChecklistEmails: find(USER_SETTINGS.RECEIVE_CHECKLIST_EMAILS) || 'true',
          staleDaysThreshold: find(USER_SETTINGS.STALE_DAYS_THRESHOLD) || '7',
        });
      })
      .catch(() => {
        // Keep defaults on error
      })
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await settingsService.setSettings([
        { name: USER_SETTINGS.FIRST_WARNING_DUE_DAYS, value: settings.firstWarningDueDays },
        { name: USER_SETTINGS.SECOND_WARNING_DUE_DAYS, value: settings.secondWarningDueDays },
        { name: USER_SETTINGS.SHOW_DASHBOARD_CHARTS, value: settings.showDashboardCharts },
        { name: USER_SETTINGS.DEFAULT_LANDING_PAGE, value: settings.defaultLandingPage },
        { name: USER_SETTINGS.CLIENT_DASHBOARD_LANDING_PAGE, value: settings.clientDashboardLandingPage },
        { name: USER_SETTINGS.JOB_EMAILS_ENABLED, value: settings.jobEmailsEnabled },
        { name: USER_SETTINGS.RECEIVE_CHECKLIST_EMAILS, value: settings.receiveChecklistEmails },
        { name: USER_SETTINGS.STALE_DAYS_THRESHOLD, value: settings.staleDaysThreshold },
      ]);
      await queryClient.invalidateQueries({ queryKey: settingsKeys.all });
      onClose();
    } catch {
      // Stay open on error so user can retry
    } finally {
      setIsSaving(false);
    }
  }, [settings, onClose, queryClient]);

  if (!isOpen) return null;

  const internalQuestionnaireLandingOptions = canOpenInternalQuestionnaire
    ? dashboardQuestionnaires.map((questionnaire) => ({
        label: questionnaire.title,
        value: getInternalQuestionnaireLandingValue(questionnaire.questionnaireId),
      }))
    : [];
  const landingPageOptions = [
    ...internalQuestionnaireLandingOptions,
    { label: t('WorkInProgress'), value: 'WorkInProgress', permission: PERMISSIONS.DASHBOARD.WIP },
    { label: t('Calendar'), value: 'calendar', permission: PERMISSIONS.DASHBOARD.CALENDAR },
    { label: t('Activity'), value: 'activity', permission: PERMISSIONS.DASHBOARD.ACTIVITIES },
    { label: t('Workload'), value: 'workload', permission: PERMISSIONS.DASHBOARD.WORKLOAD },
    { label: t('Summary'), value: 'summary', permission: PERMISSIONS.DASHBOARD.SUMMARY },
  ].filter((option) => !('permission' in option) || hasPermission(option.permission));

  const questionnaireLandingOptions = canOpenFrontDoor
    ? dashboardQuestionnaires.map((questionnaire) => ({
        label: questionnaire.title,
        value: getClientQuestionnaireLandingValue(questionnaire.questionnaireId),
      }))
    : [];
  const clientDashboardOptions = [
    ...questionnaireLandingOptions,
    {
      label: t('ClientDashboard:MyRequests'),
      value: 'requests',
      permission: PERMISSIONS.CLIENT_DASHBOARD.MY_REQUESTS,
    },
    {
      label: t('ClientDashboard:AllDocuments'),
      value: 'documents',
      permission: PERMISSIONS.CLIENT_DASHBOARD.DOCUMENTS,
    },
    {
      label: t('ClientDashboard:Activity'),
      value: 'activity',
      permission: PERMISSIONS.CLIENT_DASHBOARD.ACTIVITY,
    },
  ].filter((option) => !('permission' in option) || hasPermission(option.permission));
  const isContentLoading = isLoading || isLoadingQuestionnaires;

  const toggleGroupStyle: React.CSSProperties = {
    display: 'inline-flex',
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    border: theme.borders.default,
  };

  const getToggleButtonStyle = (isActive: boolean): React.CSSProperties => ({
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    border: 'none',
    backgroundColor: isActive ? theme.colors.brandAccent : theme.colors.backgroundCard,
    color: isActive ? 'white' : theme.colors.textPrimary,
    fontSize: theme.typography.fontSizeBase,
    fontWeight: isActive ? theme.typography.fontWeightSemibold : theme.typography.fontWeightNormal,
    fontFamily: theme.typography.fontFamily,
    cursor: 'pointer',
    outline: 'none',
    transition: `all ${theme.transitions.default}`,
    whiteSpace: 'nowrap',
  });

  const dashboardSectionStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    border: theme.borders.default,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.backgroundMuted,
  };

  const dashboardChoiceGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing.sm,
  };

  const getDashboardChoiceStyle = (isActive: boolean): React.CSSProperties => ({
    minHeight: '52px',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
    textAlign: 'left',
    border: `1px solid ${isActive ? theme.colors.brandAccent : theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    backgroundColor: isActive ? theme.colors.brandAccentLight : theme.colors.backgroundCard,
    color: isActive ? theme.colors.brandAccent : theme.colors.textPrimary,
    fontSize: theme.typography.fontSizeSm,
    fontWeight: isActive ? theme.typography.fontWeightSemibold : theme.typography.fontWeightMedium,
    fontFamily: theme.typography.fontFamily,
    cursor: 'pointer',
    transition: `border-color ${theme.transitions.default}, background-color ${theme.transitions.default}`,
  });

  const selectionIndicator = (isActive: boolean) => (
    <span
      aria-hidden="true"
      style={{
        width: '18px',
        height: '18px',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: `1px solid ${isActive ? theme.colors.brandAccent : theme.colors.border}`,
        backgroundColor: isActive ? theme.colors.brandAccent : theme.colors.backgroundCard,
        color: 'white',
        fontSize: '11px',
        lineHeight: 1,
      }}
    >
      {isActive ? '✓' : ''}
    </span>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.colors.overlayBackdrop,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: theme.zIndex.modal,
        padding: isMobile ? theme.spacing.md : theme.spacing.xxl,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="my-settings-title"
    >
      <div
        style={{
          backgroundColor: theme.colors.backgroundCard,
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.modal,
          width: '100%',
          maxWidth: isMobile ? '100%' : '760px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
            backgroundColor: theme.colors.dialogBackground,
            color: theme.colors.dialogFontColor,
          }}
        >
          <h2
            id="my-settings-title"
            style={{
              margin: 0,
              fontSize: theme.typography.fontSizeXl,
              fontWeight: theme.typography.fontWeightSemibold,
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {t('MySettings')}
          </h2>
          <button
            onClick={onClose}
            aria-label={t('Close')}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.dialogFontColor,
              cursor: 'pointer',
              padding: theme.spacing.xs,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: theme.borderRadius.md,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: `${theme.spacing.xl} ${theme.spacing.xl}`,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {isContentLoading ? (
            <Spinner mode="centered" text={t('Loading')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xl }}>
              {/* First Warning Due Days */}
              <div>
                <label
                  htmlFor="firstWarningDueDays"
                  style={{
                    display: 'block',
                    fontSize: theme.typography.fontSizeBase,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.sm,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('firstWarningDueDays')}
                </label>
                <input
                  id="firstWarningDueDays"
                  type="number"
                  min="0"
                  value={settings.firstWarningDueDays}
                  onChange={(e) => setSettings((s) => ({ ...s, firstWarningDueDays: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    border: theme.borders.default,
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSizeBase,
                    fontFamily: theme.typography.fontFamily,
                    color: theme.colors.textPrimary,
                    backgroundColor: theme.colors.backgroundCard,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.brandAccent;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.border;
                  }}
                />
              </div>

              {/* Second Warning Due Days */}
              <div>
                <label
                  htmlFor="secondWarningDueDays"
                  style={{
                    display: 'block',
                    fontSize: theme.typography.fontSizeBase,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.sm,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('secondWarningDueDays')}
                </label>
                <input
                  id="secondWarningDueDays"
                  type="number"
                  min="0"
                  value={settings.secondWarningDueDays}
                  onChange={(e) => setSettings((s) => ({ ...s, secondWarningDueDays: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    border: theme.borders.default,
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSizeBase,
                    fontFamily: theme.typography.fontFamily,
                    color: theme.colors.textPrimary,
                    backgroundColor: theme.colors.backgroundCard,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.brandAccent;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.border;
                  }}
                />
              </div>

              {/* Stale Days Threshold */}
              <div>
                <div
                  style={{
                    fontSize: theme.typography.fontSizeBase,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.sm,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('StaleDaysThreshold')}
                </div>
                <div
                  style={{
                    ...toggleGroupStyle,
                    display: 'grid',
                    width: '100%',
                    gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
                    gap: '1px',
                    backgroundColor: theme.colors.border,
                  }}
                  role="group"
                  aria-label={t('StaleDaysThreshold')}
                >
                  {[
                    { label: '7', value: '7' },
                    { label: '14', value: '14' },
                    { label: '21', value: '21' },
                    { label: '30', value: '30' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSettings((s) => ({ ...s, staleDaysThreshold: opt.value }))}
                      style={getToggleButtonStyle(settings.staleDaysThreshold === opt.value)}
                      aria-pressed={settings.staleDaysThreshold === opt.value}
                    >
                      {opt.label} {t('StepDuration:Days')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Internal Dashboard */}
              <div style={dashboardSectionStyle}>
                <div
                  style={{
                    fontSize: theme.typography.fontSizeBase,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.md,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('InternalDashboard', { defaultValue: 'Internal Dashboard' })}
                </div>
                <div
                  style={dashboardChoiceGridStyle}
                  role="group"
                  aria-label={t('InternalDashboard', { defaultValue: 'Internal Dashboard' })}
                >
                  {landingPageOptions.map((opt) => {
                    const isLegacyWorkInProgressSelection =
                      opt.value === 'WorkInProgress'
                      && ['todo', 'wip'].includes(settings.defaultLandingPage);
                    const isActive = settings.defaultLandingPage === opt.value || isLegacyWorkInProgressSelection;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSettings((s) => ({ ...s, defaultLandingPage: opt.value }))}
                        style={getDashboardChoiceStyle(isActive)}
                        aria-pressed={isActive}
                      >
                        <span>{opt.label}</span>
                        {selectionIndicator(isActive)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Client Dashboard */}
              <div style={dashboardSectionStyle}>
                <div
                  style={{
                    fontSize: theme.typography.fontSizeBase,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.xs,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('ClientDashboard', { defaultValue: 'Client Dashboard' })}
                </div>
                <div
                  style={{
                    fontSize: theme.typography.fontSizeSm,
                    color: theme.colors.textSecondary,
                    marginBottom: theme.spacing.sm,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('ClientDashboardLandingPageHint', { defaultValue: 'Choose where the client workspace opens.' })}
                </div>
                <div
                  style={dashboardChoiceGridStyle}
                  role="group"
                  aria-label={t('ClientDashboard', { defaultValue: 'Client Dashboard' })}
                >
                  {clientDashboardOptions.map((opt, index) => {
                    const isLegacyFrontDoorSelection =
                      settings.clientDashboardLandingPage === 'front-door'
                      && index === 0
                      && questionnaireLandingOptions.length > 0;
                    const isActive = settings.clientDashboardLandingPage === opt.value || isLegacyFrontDoorSelection;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSettings((s) => ({ ...s, clientDashboardLandingPage: opt.value }))}
                        style={getDashboardChoiceStyle(isActive)}
                        aria-pressed={isActive}
                      >
                        <span>{opt.label}</span>
                        {selectionIndicator(isActive)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Job Emails */}
              <div>
                <div
                  style={{
                    fontSize: theme.typography.fontSizeBase,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.sm,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('JobEmailsEnabled')}
                </div>
                <div style={toggleGroupStyle} role="group" aria-label={t('JobEmailsEnabled')}>
                  <button
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, jobEmailsEnabled: 'true' }))}
                    style={getToggleButtonStyle(settings.jobEmailsEnabled === 'true')}
                    aria-pressed={settings.jobEmailsEnabled === 'true'}
                  >
                    {t('AllowEmail')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, jobEmailsEnabled: 'false' }))}
                    style={getToggleButtonStyle(settings.jobEmailsEnabled === 'false')}
                    aria-pressed={settings.jobEmailsEnabled === 'false'}
                  >
                    {t('NoEmails')}
                  </button>
                </div>
              </div>

              {/* Checklist Emails */}
              <div>
                <div
                  style={{
                    fontSize: theme.typography.fontSizeBase,
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.xs,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('ReceiveChecklistEmails')}
                </div>
                <div
                  style={{
                    fontSize: theme.typography.fontSizeSm,
                    color: theme.colors.textSecondary,
                    marginBottom: theme.spacing.sm,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {t('ReceiveChecklistEmailsHint')}
                </div>
                <div style={toggleGroupStyle} role="group" aria-label={t('ReceiveChecklistEmails')}>
                  <button
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, receiveChecklistEmails: 'true' }))}
                    style={getToggleButtonStyle(settings.receiveChecklistEmails === 'true')}
                    aria-pressed={settings.receiveChecklistEmails === 'true'}
                  >
                    {t('AllowEmail')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, receiveChecklistEmails: 'false' }))}
                    style={getToggleButtonStyle(settings.receiveChecklistEmails === 'false')}
                    aria-pressed={settings.receiveChecklistEmails === 'false'}
                  >
                    {t('NoEmails')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isContentLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.spacing.md,
              padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
              borderTop: theme.borders.default,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
                backgroundColor: theme.colors.backgroundMuted,
                color: theme.colors.textPrimary,
                border: theme.borders.default,
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeBase,
                fontWeight: theme.typography.fontWeightMedium,
                fontFamily: theme.typography.fontFamily,
                cursor: 'pointer',
                minWidth: '90px',
              }}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
                backgroundColor: theme.colors.brandAccent,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                fontSize: theme.typography.fontSizeBase,
                fontWeight: theme.typography.fontWeightMedium,
                fontFamily: theme.typography.fontFamily,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
                minWidth: '90px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.sm,
              }}
            >
              {isSaving && <Spinner mode="inline" size="xs" />}
              {t('Save')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
